/* eslint-disable no-nested-ternary */
import dayjs from 'dayjs';
import { OutboundProcess } from '@features/supplyChain/types';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '@context';
import { PalletCalculator } from '@components/PalletCalculator';
import { PackagingBreakdown } from '@components/PackagingBreakdown';
import { UserRoleService } from '@services';
import { numberToLocale } from '@forms/client/AddProductForm/utils';
import { SkuInfoLink } from '@tailwindComponents/SkuInfoModal';
import { Button } from '../../../tailwindComponents/Button';
import { skuLinePlanFor } from '../../../utils/despatch';
import { ProductLinesSection } from './ProductLinesSection';

interface DespatchNoteDetailsProps {
  selectedOutboundProcess: OutboundProcess;
  onClose: () => void;
  primaryAction?: () => void;
  embedded?: boolean;
  isDesktop: boolean;
  isTablet: boolean;
}

function CopyIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <rect width='14' height='14' x='8' y='8' rx='2' ry='2' />
      <path d='M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2' />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' />
      <circle cx='9' cy='7' r='4' />
      <path d='M22 21v-2a4 4 0 0 0-3-3.87' />
      <path d='M16 3.13a4 4 0 0 1 0 7.75' />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <rect width='18' height='18' x='3' y='4' rx='2' ry='2' />
      <line x1='16' x2='16' y1='2' y2='6' />
      <line x1='8' x2='8' y1='2' y2='6' />
      <line x1='3' x2='21' y1='10' y2='10' />
    </svg>
  );
}

function LocationIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z' />
      <circle cx='12' cy='10' r='3' />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='18'
      height='18'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <polyline points='20 6 9 17 4 12' />
    </svg>
  );
}

export function DespatchNoteDetails({
  selectedOutboundProcess,
  onClose,
  primaryAction,
  embedded,
  isDesktop,
  isTablet,
}: DespatchNoteDetailsProps) {
  const { despatchNote, pickingNote, notices, goodsReceivedNote } =
    selectedOutboundProcess;
  const { user } = useAuth();
  const { isWarehouseManager } = UserRoleService();

  if (!despatchNote) {
    return null;
  }

  const hasIssues = (notices?.length ?? 0) > 0;
  const isProcessComplete = !!goodsReceivedNote?.finalisedAt;
  const hasIssuesBeforeProcessIsComplete = hasIssues && !isProcessComplete;

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(despatchNote.despatchNoteNumber);
  };

  return (
    <div className='flex flex-col'>
      {/* Document Reference Badge */}
      <div className='px-6 pt-2 pb-4'>
        <div className='inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-md border border-gray-200'>
          <span className='text-sm font-medium text-gray-700'>
            Despatch note #{despatchNote.despatchNoteNumber}
          </span>
          <button
            type='button'
            onClick={handleCopyToClipboard}
            className='text-gray-500 hover:text-gray-700 transition-colors'
            aria-label='Copy despatch note number'
          >
            <CopyIcon />
          </button>
        </div>
      </div>

      {/* Metadata Fields */}
      <div
        className={`grid gap-4 px-6 py-4 border-b border-gray-200 ${
          isDesktop ? 'grid-cols-4' : isTablet ? 'grid-cols-2' : 'grid-cols-1'
        }`}
      >
        {/* Creation date */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <CalendarIcon />
            <span className='text-xs font-medium'>Creation date</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {dayjs(despatchNote.createdAt).format('D MMM YYYY')}
          </span>
        </div>

        {/* Customer name */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <UserIcon />
            <span className='text-xs font-medium'>Customer name</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {despatchNote.customer?.name ?? despatchNote.customerName ?? 'N/A'}
          </span>
        </div>

        {/* Required ETA */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <CalendarIcon />
            <span className='text-xs font-medium'>Required ETA</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {dayjs
              .utc(despatchNote.requiredTimeOfArrival.dateTime)
              .tz(despatchNote.requiredTimeOfArrival.timeZone)
              .format('D MMM YYYY')}
          </span>
        </div>
      </div>

      {/* Additional Info Row */}
      <div
        className={`grid gap-4 px-6 py-4 border-b border-gray-200 ${
          isDesktop ? 'grid-cols-4' : isTablet ? 'grid-cols-2' : 'grid-cols-1'
        }`}
      >
        {/* Client name (for warehouse managers) */}
        {isWarehouseManager(user) && (
          <div className='flex flex-col gap-1'>
            <div className='flex items-center gap-1.5 text-gray-500'>
              <UserIcon />
              <span className='text-xs font-medium'>Client name</span>
            </div>
            <span className='text-sm font-medium text-gray-900'>
              {selectedOutboundProcess.userBusinessName}
            </span>
          </div>
        )}

        {/* Delivery address */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <LocationIcon />
            <span className='text-xs font-medium'>Delivery address</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {despatchNote.customer?.fullAddress ?? 'N/A'}
          </span>
        </div>
      </div>

      {/* Issues */}
      {hasIssues && (
        <div className='px-6 py-4 border-b border-gray-200'>
          <p className='text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-2'>
            Issues
          </p>
          <div className='space-y-2'>
            {notices?.map((notice) => (
              <div
                key={notice}
                className={`flex items-center gap-2 p-2 rounded ${
                  hasIssuesBeforeProcessIsComplete ? 'bg-amber-50' : 'bg-red-50'
                }`}
              >
                <ExclamationTriangleIcon
                  className={`w-4 h-4 flex-shrink-0 ${
                    hasIssuesBeforeProcessIsComplete
                      ? 'text-amber-600'
                      : 'text-red-600'
                  }`}
                />
                <span
                  className={`text-sm ${
                    hasIssuesBeforeProcessIsComplete
                      ? 'text-amber-800'
                      : 'text-red-800'
                  }`}
                >
                  {notice}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SKU Table */}
      <div className='px-6 py-4'>
        <h3 className='text-sm font-semibold text-gray-900 mb-4'>Items</h3>

        {despatchNote.skus.length > 0 && isDesktop && (
          <div className='grid grid-cols-[2.5rem_1fr_1.5fr_1fr_1fr_1.5fr] gap-4 pb-3 border-b border-gray-200 mb-4'>
            <span className='text-xs font-semibold text-gray-500 text-center'>
              #
            </span>
            <span className='text-xs font-semibold text-gray-500'>SKU ID</span>
            <span className='text-xs font-semibold text-gray-500'>
              SKU name
            </span>
            <span className='text-xs font-semibold text-gray-500'>
              Quantity
            </span>
            <span className='text-xs font-semibold text-gray-500'>Pallets</span>
            <span className='text-xs font-semibold text-gray-500'>
              Pallet info
            </span>
          </div>
        )}

        {/* SKU Rows */}
        {despatchNote.skus.length > 0 && (
          <div className='flex flex-col gap-4'>
            {despatchNote.skus.map((sku, index) => {
              const plannedLine = skuLinePlanFor(
                pickingNote?.plannedLines,
                sku.skuId
              );
              const productFactor = sku.amountOfProductInSku;
              const looseProducts = productFactor
                ? Math.max(
                    0,
                    (plannedLine?.productsToShip ?? 0) -
                      (plannedLine?.wholeUnitsToPick ?? 0) * productFactor
                  )
                : plannedLine?.looseToTake ?? 0;
              const hasBreakdown =
                (plannedLine?.packaging?.length ?? 0) > 0 || looseProducts > 0;

              return (
                <div
                  key={sku.skuId}
                  className={`${
                    index !== despatchNote.skus.length - 1
                      ? 'pb-4 border-b border-gray-100'
                      : ''
                  }`}
                >
                  {isDesktop ? (
                    /* Desktop Layout */
                    <div className='flex flex-col gap-3'>
                      <div className='grid grid-cols-[2.5rem_1fr_1.5fr_1fr_1fr_1.5fr] gap-4 items-start'>
                        {/* Index */}
                        <div className='flex justify-center'>
                          <span className='inline-flex items-center justify-center w-7 h-7 text-xs font-medium text-gray-700 bg-gray-100 rounded-full'>
                            {index + 1}
                          </span>
                        </div>
                        {/* SKU ID */}
                        <span className='text-sm text-gray-900'>
                          <SkuInfoLink inventorySkuId={sku.skuId}>
                            {sku.skuIdentifier}
                          </SkuInfoLink>
                        </span>
                        {/* SKU name */}
                        <span className='text-sm text-gray-900'>
                          {sku.name}
                        </span>
                        {/* Quantity */}
                        <span className='text-sm text-gray-900'>
                          {numberToLocale(sku.skuQuantity)}
                        </span>
                        {/* Pallets */}
                        <div className='text-sm text-gray-900'>
                          {sku.packagingDetails.packagingInfo ? (
                            <PalletCalculator
                              palletName={
                                sku.packagingDetails.packagingInfo.type
                              }
                              skuQuantity={sku.skuQuantity}
                              amountOfSkusInLayer={
                                sku.packagingDetails.packagingInfo
                                  .amountOfSkusInLayer
                              }
                              amountOfLayersInPallet={
                                sku.packagingDetails.packagingInfo
                                  .amountOfLayersInPallet
                              }
                            />
                          ) : (
                            <span>{sku.packagingDetails.palletisingType}</span>
                          )}
                        </div>
                        {/* Pallet description */}
                        <span className='text-sm text-gray-600'>
                          {sku.packagingDetails.palletisingDescription}
                        </span>
                      </div>

                      {hasBreakdown && (
                        <div className='grid grid-cols-[2.5rem_1fr] gap-4'>
                          <div />
                          <div className='flex flex-col gap-0.5'>
                            <span className='text-xs font-medium text-gray-500'>
                              Breakdown
                            </span>
                            <PackagingBreakdown
                              packaging={plannedLine?.packaging}
                              looseProducts={looseProducts}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Mobile/Tablet Layout */
                    <div className='flex gap-3'>
                      {/* Index */}
                      <div className='flex-shrink-0'>
                        <span className='inline-flex items-center justify-center w-7 h-7 text-xs font-medium text-gray-700 bg-gray-100 rounded-full'>
                          {index + 1}
                        </span>
                      </div>
                      {/* Details */}
                      <div
                        className={`flex-1 grid gap-3 ${
                          isTablet ? 'grid-cols-2' : 'grid-cols-1'
                        }`}
                      >
                        <div className='flex flex-col gap-0.5'>
                          <span className='text-xs font-medium text-gray-500'>
                            SKU ID
                          </span>
                          <span className='text-sm text-gray-900'>
                            <SkuInfoLink inventorySkuId={sku.skuId}>
                              {sku.skuIdentifier}
                            </SkuInfoLink>
                          </span>
                        </div>
                        <div className='flex flex-col gap-0.5'>
                          <span className='text-xs font-medium text-gray-500'>
                            SKU name
                          </span>
                          <span className='text-sm text-gray-900'>
                            {sku.name}
                          </span>
                        </div>
                        <div className='flex flex-col gap-0.5'>
                          <span className='text-xs font-medium text-gray-500'>
                            Quantity
                          </span>
                          <span className='text-sm text-gray-900'>
                            {numberToLocale(sku.skuQuantity)}
                          </span>
                        </div>
                        <div className='flex flex-col gap-0.5'>
                          <span className='text-xs font-medium text-gray-500'>
                            Pallets
                          </span>
                          <div className='text-sm text-gray-900'>
                            {sku.packagingDetails.packagingInfo ? (
                              <PalletCalculator
                                palletName={
                                  sku.packagingDetails.packagingInfo.type
                                }
                                skuQuantity={sku.skuQuantity}
                                amountOfSkusInLayer={
                                  sku.packagingDetails.packagingInfo
                                    .amountOfSkusInLayer
                                }
                                amountOfLayersInPallet={
                                  sku.packagingDetails.packagingInfo
                                    .amountOfLayersInPallet
                                }
                              />
                            ) : (
                              <span>
                                {sku.packagingDetails.palletisingType}
                              </span>
                            )}
                          </div>
                        </div>
                        {sku.packagingDetails.palletisingDescription && (
                          <div
                            className={`flex flex-col gap-0.5 ${
                              isTablet ? 'col-span-2' : ''
                            }`}
                          >
                            <span className='text-xs font-medium text-gray-500'>
                              Pallet description
                            </span>
                            <span className='text-sm text-gray-600'>
                              {sku.packagingDetails.palletisingDescription}
                            </span>
                          </div>
                        )}
                        {hasBreakdown && (
                          <div
                            className={`flex flex-col gap-0.5 ${
                              isTablet ? 'col-span-2' : ''
                            }`}
                          >
                            <span className='text-xs font-medium text-gray-500'>
                              Breakdown
                            </span>
                            <PackagingBreakdown
                              packaging={plannedLine?.packaging}
                              looseProducts={looseProducts}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {(despatchNote.products?.length ?? 0) > 0 && (
          <div className={despatchNote.skus.length > 0 ? 'mt-6' : ''}>
            <h4 className='text-sm font-semibold text-gray-900 mb-4'>
              Products
            </h4>

            <ProductLinesSection
              products={despatchNote.products ?? []}
              plannedLines={pickingNote?.plannedLines}
              isDesktop={isDesktop}
              isTablet={isTablet}
            />
          </div>
        )}
      </div>

      {/* Comments Section */}
      {despatchNote.comment && (
        <div className='px-6 py-4 border-t border-gray-200'>
          <h3 className='text-sm font-semibold text-gray-900 mb-2'>Comment</h3>
          <p className='text-sm text-gray-600'>{despatchNote.comment}</p>
        </div>
      )}

      {!embedded && (
        <div
          className={`flex gap-3 px-6 py-4 mt-4 border-t border-gray-200 ${
            isDesktop ? 'flex-row justify-end' : 'flex-col-reverse'
          }`}
        >
          <Button
            label='Close'
            intent='secondary'
            size='sm'
            onClick={onClose}
            className={!isDesktop ? 'w-full' : ''}
          />
          {isWarehouseManager(user) &&
            (!selectedOutboundProcess?.despatchNote?.isAuthorisedForPicking ||
              (selectedOutboundProcess?.despatchNote?.isAuthorisedForPicking &&
                !selectedOutboundProcess?.pickingConfirmation &&
                primaryAction)) && (
              <Button
                label={
                  !selectedOutboundProcess?.despatchNote?.isAuthorisedForPicking
                    ? 'Authorise For Picking'
                    : 'Confirm Picking'
                }
                intent='primary'
                size='sm'
                onClick={primaryAction}
                startIcon={<CheckIcon />}
                className={!isDesktop ? 'w-full' : ''}
              />
            )}
        </div>
      )}
    </div>
  );
}
